import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, get } from "../../api/client";
import type { Run } from "../../api/types";

/**
 * **Einen Lauf verfolgen**: Solange er läuft, alle zwei Sekunden nachfragen.
 * Ein eigener `setTimeout` statt `refetchInterval`, weil der Lauf wechseln
 * kann (Abbrechen startet einen Rückbau mit neuer ID) und weil ein 404 eine
 * Antwort ist: Ein gelöschter Mandant nimmt seine Läufe mit — dann ist der
 * Lauf vorbei.
 *
 * `onFinished` läuft einmal, wenn der Lauf von „läuft" in etwas anderes
 * wechselt — und nicht mehr, wenn die Karte inzwischen weg ist.
 */
export function useRunPolling(tenantId: number, run: Run, onFinished?: (run: Run) => void) {
  const [current, setCurrent] = useState(run);
  // Ein Fehlschlag ändert `current` nicht — der Zähler plant trotzdem neu.
  const [tick, setTick] = useState(0);
  const currentRef = useRef(current);
  const finishedRef = useRef(onFinished);
  const alive = useRef(true);
  useEffect(() => { currentRef.current = current; }, [current]);
  useEffect(() => { finishedRef.current = onFinished; }, [onFinished]);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const refresh = useCallback(async () => {
    const before = currentRef.current;
    try {
      const res = await get<{ run: Run }>(`/tenants/${tenantId}/runs/${before.id}`);
      if (!alive.current) return;
      currentRef.current = res.run;
      setCurrent(res.run);
      if (before.state === "running" && res.run.state !== "running") finishedRef.current?.(res.run);
    } catch (err) {
      if (!alive.current) return;
      if (err instanceof ApiError && err.status === 404) {
        const done: Run = { ...before, state: "done" };
        currentRef.current = done;
        setCurrent(done);
        finishedRef.current?.(done);
        return;
      }
      setTick((t) => t + 1);
    }
  }, [tenantId]);

  useEffect(() => {
    if (current.state !== "running") return;
    const timer = setTimeout(() => void refresh(), 2000);
    return () => clearTimeout(timer);
  }, [current, tick, refresh]);

  const replace = useCallback((next: Run) => {
    currentRef.current = next;
    setCurrent(next);
  }, []);

  return { current, refresh, replace };
}
